'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Copies text to the clipboard and flips a `copied` flag for `duration` ms so
 * the UI can show a "Copied!" confirmation. Falls back to window.prompt where
 * the async clipboard API is unavailable (older/insecure contexts).
 */
export function useCopyToClipboard(duration = 1600) {
  const [copied, setCopied] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  useEffect(() => () => clearTimeout(timer.current), [])

  const copy = useCallback(
    async (text: string) => {
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(text)
        } else {
          window.prompt('Copy this value:', text)
        }
      } catch {
        window.prompt('Copy this value:', text)
      }
      setCopied(true)
      clearTimeout(timer.current)
      timer.current = setTimeout(() => setCopied(false), duration)
    },
    [duration],
  )

  return { copied, copy }
}
